import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import httpClient from "api/httpClient";

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function CustomerResetPin() {
  const query = useQuery();
  const navigate = useNavigate();

  const token = query.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const [pinError, setPinError] = useState(false);
  const [confirmError, setConfirmError] = useState(false);

  const [saving, setSaving] = useState(false);

  const [sb, setSb] = useState({ open: false, color: "error", title: "", message: "" });

  const showSnack = (title, color, message) => setSb({ open: true, title, color, message });
  const closeSnack = () => setSb((s) => ({ ...s, open: false }));

  const [messageState, setMessageState] = useState("error");

  const [displayMessage, setDisplayMessage] = useState("This reset link is invalid or expired.");

  const validateForm = () => {
    let ok = true;

    const p = (pin || "").trim();
    const c = (confirmPin || "").trim();

    const isDigits = /^\d+$/.test(p);
    const lenOk = p.length >= 4 && p.length <= 6;

    if (!p || !isDigits || !lenOk) {
      setPinError(true);
      ok = false;
    } else setPinError(false);

    if (!c || c !== p) {
      setConfirmError(true);
      ok = false;
    } else setConfirmError(false);

    if (!ok) {
      if (!p) showSnack("Invalid PIN", "error", "PIN is required.");
      else if (!isDigits) showSnack("Invalid PIN", "error", "PIN must be numeric.");
      else if (!lenOk) showSnack("Invalid PIN", "error", "PIN must be 4 to 6 digits.");
      else showSnack("Invalid PIN", "error", "PINs do not match.");
    }

    return ok;
  };

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setChecking(false);
        setValid(false);
        showSnack("Missing token", "error", "Reset token is missing from the link.");
        return;
      }

      setChecking(true);
      try {
        const res = await httpClient.get(
          `/auth/reset-pin/validate?token=${encodeURIComponent(token)}`
        );
        setValid(Boolean(res.data?.valid));
        if (!res.data?.valid) {
          showSnack("Invalid link", "error", "This reset link is invalid or has expired.");
        }
      } catch (e) {
        setValid(false);
        showSnack(
          "Error",
          "error",
          e?.response?.data?.message || e.message || "Failed to validate link"
        );
      } finally {
        setChecking(false);
      }
    };

    run();
  }, [token]);

  const submit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      await httpClient.post("/auth/reset-pin", {
        token,
        pin: pin.trim(),
        confirm_pin: confirmPin.trim(),
      });

      let msg = "PIN reset successfully! You can now approve transactions.";
      showSnack("Success", "success", msg);
      // Redirect back to sign-in after short delay
      //setTimeout(() => navigate("/authentication/sign-in", { replace: true }), 900);

      setMessageState("success");
      setDisplayMessage(msg);
      setValid(false); // hide form after success
    } catch (e) {
      showSnack("Failed", "error", e?.response?.data?.message || e.message || "Reset failed");
      setMessageState("error");
      setDisplayMessage(e?.response?.data?.message || e.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Reset PIN
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {checking ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          ) : !valid ? (
            <MDBox textAlign="center" py={2}>
              <MDTypography variant="button" color={messageState} fontWeight="regular">
                {displayMessage}
              </MDTypography>
              <MDBox mt={3}></MDBox>
            </MDBox>
          ) : (
            <MDBox component="form" role="form">
              <MDTypography variant="button" color="text" fontWeight="regular">
                Choose a new PIN (4 digits).
              </MDTypography>

              <MDBox mt={2}>
                <MDInput
                  type="password"
                  label="New PIN"
                  fullWidth
                  value={pin}
                  error={pinError}
                  onChange={(e) => setPin(e.target.value)}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />
              </MDBox>

              <MDBox mt={2}>
                <MDInput
                  type="password"
                  label="Confirm PIN"
                  fullWidth
                  value={confirmPin}
                  error={confirmError}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  onClick={submit}
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={saving}
                  sx={{ height: "45px" }}
                >
                  {saving ? <CircularProgress size={24} color="white" /> : "Set PIN"}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>

      <MDSnackbar
        color={sb.color}
        icon={sb.color === "error" ? "warning" : "check_circle"}
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={closeSnack}
        close={closeSnack}
        bgWhite
      />
    </BasicLayout>
  );
}
